import type { Store } from "@hobbyshopify/backend-client";
import { BaseController, type Rx, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import { updateStore } from "../../../../core/ops/store-ops";
import { hasErrors } from "../../../../core/primitives/form-errors";
import { busyForm, failedForm, idleForm } from "../../../../core/primitives/load-status";
import { hostsShopPage } from "../../../../core/primitives/shop-page";
import {
  draftToRequest,
  type ProfileField,
  storeToDraft,
  validateProfileDraft,
} from "../../../../core/primitives/store-profile";
import type { LogoFieldController } from "../../../../sections/logo-field/logo-field-controller";
import { ProfileSectionActions, ProfileSectionState } from "./profile-section-model";

export type ProfileSectionDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository">;

export interface ProfileSectionHost {
  /** The shop being edited; the form re-seeds whenever it changes. */
  readonly store: Rx<Store | null>;
  /** The page's logo control: seeded with the shop and read back on save. */
  readonly logo: LogoFieldController;
}

/** The shop's business profile: name, description, logo and currency. */
export class ProfileSectionController extends BaseController implements ProfileSectionActions {
  readonly state = new ProfileSectionState();
  #seededFrom: Store | null | undefined;

  constructor(
    subtree: SubtreeModel,
    readonly deps: ProfileSectionDeps,
    readonly host: ProfileSectionHost,
  ) {
    super();
    subtree.put(ProfileSectionState, this.state);
    subtree.put(ProfileSectionActions, this);
    this.sync(() => {
      const store = host.store.value;
      this.state.hostsPage.value = hostsShopPage(store);
      if (store !== this.#seededFrom) this.#seed(store);
    }, [host.store]);
  }

  setField = (field: ProfileField, value: string) => {
    this.state.draft.update((d) => ({ ...d, [field]: value }));
    if (this.state.errors.value[field]) this.state.errors.update(({ [field]: _, ...rest }) => rest);
  };

  save = async () => {
    const store = this.host.store.value;
    if (!store || this.state.form.value.busy) return;
    const draft = { ...this.state.draft.value, logoUrl: this.host.logo.state.url.value };
    const errors = validateProfileDraft(draft, { requireDescription: hostsShopPage(store) });
    this.state.errors.value = errors;
    if (hasErrors(errors)) return;
    this.state.form.value = busyForm;
    const r = await updateStore({ ...this.deps, storeId: store.id, body: draftToRequest(draft, true) });
    if (this.isDisposed) return;
    this.state.form.value = r.ok ? idleForm : failedForm(r.errors);
  };

  #seed(store: Store | null): void {
    this.#seededFrom = store;
    this.state.draft.value = storeToDraft(store);
    this.state.errors.value = {};
    this.host.logo.seed(store?.logoUrl);
  }
}
